import { ApiResponse } from "../../../types/api";
import { apiClient } from "../../apiClient";

import {
  Game,
  GameApiResponse,
  CreateGameRequest,
  UpdateGameRequest,
  GetGamesParams,
  GetGamesResponse,
} from "./type";

class GameService {
  private transformGame(apiGame: GameApiResponse): Game {
    return {
      id: apiGame.id,
      name: apiGame.name,
      createdAt: apiGame.createdAt,
      updatedAt: apiGame.updatedAt,
      isActive: apiGame.isActive,
      createdBy: apiGame.createdBy,
      updatedBy: apiGame.updatedBy,
    };
  }

  async getGames(params: GetGamesParams = {}): Promise<GetGamesResponse> {
    try {
      const queryParams = new URLSearchParams();

      if (params.skip !== undefined) {
        queryParams.append("skip", params.skip.toString());
      }
      if (params.limit !== undefined) {
        queryParams.append("limit", params.limit.toString());
      }
      if (params.search) {
        queryParams.append("search", params.search);
      }

      const response = await apiClient.get<ApiResponse<GetGamesResponse>>(
        `/games?${queryParams.toString()}`,
      );

      return {
        games: response.data.data.games.map(this.transformGame),
        total: response.data.data.total,
        skip: response.data.data.skip,
        limit: response.data.data.limit,
      };
    } catch (error) {
      console.error("Failed to fetch games:", error);
      throw error;
    }
  }

  async getGame(id: string): Promise<Game> {
    try {
      const response = await apiClient.get<ApiResponse<GameApiResponse>>(
        `/games/${id}`,
      );
      return this.transformGame(response.data.data);
    } catch (error) {
      console.error(`Failed to fetch game ${id}:`, error);
      throw error;
    }
  }

  async createGame(data: CreateGameRequest): Promise<Game> {
    try {
      const response = await apiClient.post<ApiResponse<GameApiResponse>>(
        "/games",
        data,
      );
      return this.transformGame(response.data.data);
    } catch (error) {
      console.error("Failed to create game:", error);
      throw error;
    }
  }

  async updateGame(gameId: string, data: UpdateGameRequest): Promise<Game> {
    try {
      const response = await apiClient.put<ApiResponse<GameApiResponse>>(
        `/games/${gameId}`,
        data,
      );
      return this.transformGame(response.data.data);
    } catch (error) {
      console.error(`Failed to update game ${gameId}:`, error);
      throw error;
    }
  }

  async deleteGame(id: string): Promise<void> {
    try {
      await apiClient.delete(`/games/${id}`);
    } catch (error) {
      console.error(`Failed to delete game ${id}:`, error);
      throw error;
    }
  }
}

export const gameService = new GameService();
export type {
  CreateGameRequest,
  UpdateGameRequest,
  GetGamesParams,
  GetGamesResponse,
};
